import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MdOutlinePerson,
  MdOutlineCalendarMonth,
  MdOutlineMonetizationOn,
  MdOutlineMarkUnreadChatAlt,
  MdOutlineSchool,
  MdOutlineAdminPanelSettings,
  MdOutlineLogout,
  MdOutlineLogin
} from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import { BASE_URL } from '../config/api';
import './UserMenu.css';

const UserMenu = ({ onLinkClick }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isInMobileMenu, setIsInMobileMenu] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const getAvatarUrl = () => {
    const avatarPath = user?.photo || user?.avatar;
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    return `${BASE_URL}${avatarPath.startsWith('/') ? '' : '/media/'}${avatarPath}`;
  };
  
  const getDisplayName = () => {
    const lang = i18n.language;
    if (lang === 'ka') {
      return user?.full_name_ka || user?.full_name_en || user?.full_name || user?.username;
    }
    return user?.full_name_en || user?.full_name || user?.username;
  };
  
  const getFirstNameInitial = () => {
    const lang = i18n.language;
    if (lang === 'ka' && user?.first_name_ka) {
      return user.first_name_ka.charAt(0).toUpperCase();
    }
    return (user?.first_name_en || user?.first_name || user?.username)?.charAt(0).toUpperCase();
  };

  // Close menu when clicking outside
  useEffect(() => {
    // Don't close if in mobile menu
    if (!isOpen || isInMobileMenu) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the menu and not on the trigger button
      if (menuRef.current && 
          !menuRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
          setIsOpen(false);
      }
    };

    // Add a longer delay to prevent immediate closing when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, isInMobileMenu]);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Position dropdown dynamically on desktop
  useEffect(() => {
    if (isOpen && dropdownRef.current && buttonRef.current && window.innerWidth > 768) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdown = dropdownRef.current;
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${buttonRect.bottom + 8}px`;
      dropdown.style.right = `${window.innerWidth - buttonRect.right}px`;
      dropdown.style.left = 'auto';
    }
  }, [isOpen]);

  // Check if inside mobile navigation and toggle class on parent
  useEffect(() => {
    const checkMobileMenu = () => {
      if (menuRef.current) {
        const parentNavLinks = menuRef.current.closest('.nav-links.active');
        if (parentNavLinks && window.innerWidth <= 768) {
          setIsInMobileMenu(true);
          // Add/remove class to parent nav-links based on dropdown state
          if (isOpen) {
            parentNavLinks.classList.add('user-dropdown-open');
          } else {
            parentNavLinks.classList.remove('user-dropdown-open');
          }
        } else {
          setIsInMobileMenu(false);
        }
      }
    };

    // Check periodically when component mounts
    const interval = setInterval(checkMobileMenu, 100);
    
    // Also check immediately
    checkMobileMenu();

    // Also check on window resize
    window.addEventListener('resize', checkMobileMenu);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkMobileMenu);
    };
  }, [isOpen]);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  // Close menu immediately when any link is clicked
  const handleLinkClick = () => {
    setIsOpen(false);
    if (onLinkClick) {
      onLinkClick();
    }
  };

  return (
    <div className={`user-menu-container ${isOpen ? 'dropdown-open' : ''}`} ref={menuRef}>
      <button 
        ref={buttonRef}
        className="user-menu-trigger" 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        onTouchStart={(e) => {
          // Prevent default to avoid double-tap zoom and other iOS behaviors
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(prev => !prev);
          }
        }}
        aria-label="User menu"
        aria-expanded={isOpen}
        type="button"
      >
        {isAuthenticated() ? (
          <>
            {getAvatarUrl() ? (
              <img 
                src={getAvatarUrl()} 
                alt={getDisplayName()} 
                className="user-avatar"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const placeholder = e.target.nextSibling;
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }}
                draggable="false"
              />
            ) : null}
            <div 
              className="user-avatar-placeholder"
              style={{ display: getAvatarUrl() ? 'none' : 'flex' }}
            >
              {getFirstNameInitial()}
            </div>
          </>
        ) : (
          <div className="user-avatar-placeholder guest">
            <MdOutlinePerson size={20} />
          </div>
        )}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" ref={dropdownRef}>
          {isAuthenticated() ? (
            <>
              <div 
                className="user-menu-header"
                onClick={(e) => {
                  // On mobile, clicking the header toggles the dropdown
                  if (isInMobileMenu && typeof window !== 'undefined' && window.innerWidth <= 768) {
                    e.stopPropagation();
                    setIsOpen(prev => !prev);
                  }
                }}
                style={{ cursor: isInMobileMenu && typeof window !== 'undefined' && window.innerWidth <= 768 ? 'pointer' : 'default' }}
              >
                <div className="user-menu-avatar-container">
                  {getAvatarUrl() ? (
                    <img 
                      src={getAvatarUrl()} 
                      alt={getDisplayName()} 
                      className="user-menu-avatar"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextSibling;
                        if (placeholder) {
                          placeholder.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div 
                    className="user-menu-avatar-placeholder"
                    style={{ display: getAvatarUrl() ? 'none' : 'flex' }}
                  >
                    {getFirstNameInitial()}
                  </div>
                </div>
                <div className="user-menu-info">
                  <div className="user-menu-name">{getDisplayName()}</div>
                  <div className="user-menu-email">{user?.email}</div>
                </div>
              </div>
              <div className="user-menu-divider"></div>
              <Link to="/profile" className="user-menu-item" onClick={handleLinkClick}>
                <MdOutlinePerson size={16} />
                {t('nav.profile')}
              </Link>
              {(user?.role === 'member' || user?.role === 'admin') && (
                <>
                  <Link to="/my-events" className="user-menu-item" onClick={handleLinkClick}>
                    <MdOutlineCalendarMonth size={16} />
                    {t('nav.myEvents')}
                  </Link>
                  <Link to="/earnings" className="user-menu-item" onClick={handleLinkClick}>
                    <MdOutlineMonetizationOn size={16} />
                    {t('nav.earnings')}
                  </Link>
                  <Link to="/chats" className="user-menu-item" onClick={handleLinkClick}>
                    <MdOutlineMarkUnreadChatAlt size={16} />
                    {t('nav.chats')}
                  </Link>
                  <Link to="/courses" className="user-menu-item" onClick={handleLinkClick}>
                    <MdOutlineSchool size={16} />
                    {i18n.language === 'ka' ? 'სასწავლო კურსები' : 'Study courses'}
                  </Link>

                  {user?.role === 'admin' && (
                    <Link to="/admin/finances-panel" className="user-menu-item" onClick={handleLinkClick}>
                      <MdOutlineAdminPanelSettings size={16} />
                      {i18n.language === 'ka' ? 'ადმინ პანელი' : 'Admin Panel'}
                    </Link>
                  )}
                </>
              )}
              <div className="user-menu-divider"></div>
              <button className="user-menu-item logout" onClick={handleLogout}>
                <MdOutlineLogout size={16} />
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              {isOpen && (
            <>
              <Link to="/login" className="user-menu-item" onClick={handleLinkClick}>
                <MdOutlineLogin size={16} />
                {t('nav.login')}
              </Link>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UserMenu;

